import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import API from "../api/api";
import { AuthContext } from "../context/AuthContext";

const REFRESH_MS = 4000;
const WARFC_BASE = "https://warfc.com";

const EVENT_TIMEZONE = "Asia/Ulaanbaatar";
const EVENT_UTC_OFFSET = "+08:00";
const RING_START_TIME = "09:00";

const TURNOVER_MINUTES = 2;

function normalizeAgeDivision(value = "") {
  return String(value)
    .trim()
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, "");
}

function getExpectedBoutMinutes(ageDivision) {
  const age = normalizeAgeDivision(ageDivision);

  if (["6-7", "8-9", "10-11"].includes(age)) return 4;
  if (["12-13", "14-15"].includes(age)) return 6;
  if (["16-17"].includes(age)) return 8;
  if (["18-40", "18+", "+18"].includes(age)) return 11;

  return 8;
}

function getExpectedSlotMinutes(match) {
  return (
    getExpectedBoutMinutes(match?.age_division) +
    TURNOVER_MINUTES
  );
}

function getTournamentDateKey(value) {
  if (!value) return null;

  const raw = String(value).trim();
  const direct = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (direct) return `${direct[1]}-${direct[2]}-${direct[3]}`;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: EVENT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsed);

  const get = (type) => parts.find((part) => part.type === type)?.value;
  const year = get("year");
  const month = get("month");
  const day = get("day");

  return year && month && day ? `${year}-${month}-${day}` : null;
}

function getRingStartDate(tournament) {
  const dateKey = getTournamentDateKey(
    tournament?.startdate || tournament?.start_date
  );

  if (!dateKey) return null;

  return new Date(`${dateKey}T${RING_START_TIME}:00${EVENT_UTC_OFFSET}`);
}

function formatEventTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EVENT_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function getMatchTimeKey(match) {
  return String(
    match?.id ??
      `${Number(match?.ring_number || 1)}:${Number(match?.fight_number || 0)}`
  );
}

function isFinalFight(match) {
  return String(match?.round_label || "")
    .trim()
    .toUpperCase() === "FINAL";
}


function getMedalStage(match) {
  const label = String(match?.round_label || "")
    .trim()
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (label.includes("SEMI") && label.includes("FINAL")) {
    return "semifinal";
  }

  if (
    label === "FINAL" ||
    label === "FINALS" ||
    label.endsWith(" FINAL")
  ) {
    return "final";
  }

  return null;
}

function getClubForFighter(match, fighterId) {
  if (fighterId === null || fighterId === undefined) return null;

  const fighterKey = String(fighterId);

  if (String(match?.red_fighter ?? "") === fighterKey) {
    const clubName = String(match?.red_club_name || "").trim();
    const clubId =
      match?.red_club_id ??
      match?.red_fighter_club_id ??
      null;

    if (!clubName && !clubId) return null;

    return {
      clubId,
      clubName: clubName || `Club ${clubId}`,
      clubLogo: getAssetUrl(match?.red_club_logo, "clubs"),
    };
  }

  if (String(match?.blue_fighter ?? "") === fighterKey) {
    const clubName = String(match?.blue_club_name || "").trim();
    const clubId =
      match?.blue_club_id ??
      match?.blue_fighter_club_id ??
      null;

    if (!clubName && !clubId) return null;

    return {
      clubId,
      clubName: clubName || `Club ${clubId}`,
      clubLogo: getAssetUrl(match?.blue_club_logo, "clubs"),
    };
  }

  return null;
}

function buildLiveClubStandings(matches = [], standingsType = "main") {
  const clubs = new Map();

  const includedMatches = matches.filter((match) => {
    const isAltanGaraa = getDivisionTypeLabel(match?.type) === "АЛТАН ГАРАА";

    if (standingsType === "altan_garaa") {
      return isAltanGaraa;
    }

    // Main club standings must never include Altan Garaa results.
    return !isAltanGaraa;
  });

  const ensureClub = (club) => {
    if (!club) return null;

    const nameKey = String(club.clubName || "")
      .trim()
      .toLocaleLowerCase();

    // Prefer the visible club name as the stable key so the same club does not
    // split into two rows if some schedule records include club_id and others do not.
    const key = nameKey
      ? `name:${nameKey}`
      : club.clubId
      ? `id:${club.clubId}`
      : null;

    if (!key) return null;

    if (!clubs.has(key)) {
      clubs.set(key, {
        club_id: club.clubId || null,
        club_name: club.clubName || `Club ${club.clubId}`,
        club_logo: club.clubLogo || null,
        gold: 0,
        silver: 0,
        bronze: 0,
        total: 0,
      });
    }

    const row = clubs.get(key);

    if (!row.club_id && club.clubId) {
      row.club_id = club.clubId;
    }

    if (!row.club_logo && club.clubLogo) {
      row.club_logo = club.clubLogo;
    }

    return row;
  };

  const addMedal = (club, medal) => {
    const row = ensureClub(club);
    if (!row) return;

    row[medal] += 1;
    row.total += 1;
  };

  // Register every club already present in the tournament schedule so the
  // standings table can show the full field from the beginning, even at 0-0-0.
  for (const match of includedMatches) {
    if (match?.red_fighter) {
      ensureClub(getClubForFighter(match, match.red_fighter));
    }

    if (match?.blue_fighter) {
      ensureClub(getClubForFighter(match, match.blue_fighter));
    }
  }

  // Award only confirmed medals from real completed medal-round fights.
  for (const match of includedMatches) {
    if (normalizeStatus(match?.status) !== "completed") continue;

    const stage = getMedalStage(match);
    if (!stage) continue;

    const redId = match?.red_fighter;
    const blueId = match?.blue_fighter;
    const winnerId = match?.winner;

    // Never award a medal from an empty/one-sided BYE match.
    if (!redId || !blueId || !winnerId) continue;

    const winnerKey = String(winnerId);
    const redKey = String(redId);
    const blueKey = String(blueId);

    if (winnerKey !== redKey && winnerKey !== blueKey) continue;

    const loserId = winnerKey === redKey ? blueId : redId;

    if (stage === "semifinal") {
      // Every real semifinal loser earns one bronze.
      // 4+ fighter bracket -> normally two bronze medalists.
      // 3-fighter bracket -> naturally one bronze medalist.
      addMedal(getClubForFighter(match, loserId), "bronze");
      continue;
    }

    if (stage === "final") {
      addMedal(getClubForFighter(match, winnerId), "gold");
      addMedal(getClubForFighter(match, loserId), "silver");
    }
  }

  return [...clubs.values()].sort((a, b) => {
    if (b.gold !== a.gold) return b.gold - a.gold;
    if (b.silver !== a.silver) return b.silver - a.silver;
    if (b.bronze !== a.bronze) return b.bronze - a.bronze;
    if (b.total !== a.total) return b.total - a.total;

    return String(a.club_name).localeCompare(String(b.club_name));
  });
}

function applyDynamicTimesForPhase({
  matches,
  phaseStart,
  result,
}) {
  if (!Array.isArray(matches) || matches.length === 0 || !phaseStart) {
    return;
  }

  const sorted = [...matches].sort(
    (a, b) => Number(a.fight_number) - Number(b.fight_number)
  );

  // Initial plan for this phase.
  let cursor = new Date(phaseStart);

  for (const match of sorted) {
    result.set(getMatchTimeKey(match), {
      kind: "estimated",
      time: new Date(cursor),
    });

    cursor = new Date(
      cursor.getTime() + getExpectedSlotMinutes(match) * 60 * 1000
    );
  }

  // Real completed fights become authoritative anchors inside THIS phase only.
  // This is important because Day 1 semifinal timing must never pull Day 2
  // finals earlier than their fixed 09:00 start.
  let latestAnchorIndex = -1;
  let latestAnchorTime = null;

  sorted.forEach((match, index) => {
    if (normalizeStatus(match.status) !== "completed" || !match.completed_at) {
      return;
    }

    const completedAt = new Date(match.completed_at);
    if (Number.isNaN(completedAt.getTime())) return;

    result.set(getMatchTimeKey(match), {
      kind: "actual",
      time: completedAt,
    });

    if (index >= latestAnchorIndex) {
      latestAnchorIndex = index;
      latestAnchorTime = completedAt;
    }
  });

  if (latestAnchorIndex >= 0 && latestAnchorTime) {
    let dynamicCursor = new Date(
      latestAnchorTime.getTime() + TURNOVER_MINUTES * 60 * 1000
    );

    for (let index = latestAnchorIndex + 1; index < sorted.length; index += 1) {
      const match = sorted[index];

      if (normalizeStatus(match.status) === "completed" && match.completed_at) {
        const completedAt = new Date(match.completed_at);

        if (!Number.isNaN(completedAt.getTime())) {
          result.set(getMatchTimeKey(match), {
            kind: "actual",
            time: completedAt,
          });

          dynamicCursor = new Date(
            completedAt.getTime() + TURNOVER_MINUTES * 60 * 1000
          );
          continue;
        }
      }

      result.set(getMatchTimeKey(match), {
        kind: "estimated",
        time: new Date(dynamicCursor),
      });

      dynamicCursor = new Date(
        dynamicCursor.getTime() + getExpectedSlotMinutes(match) * 60 * 1000
      );
    }
  }
}

function getFinalsDayStart(dayOneStart) {
  if (!dayOneStart) return null;

  // Ulaanbaatar has no DST, so +24 hours from Day 1 09:00 is exactly
  // Day 2 09:00. This also avoids browser-local setHours timezone issues.
  return new Date(
    dayOneStart.getTime() + 24 * 60 * 60 * 1000
  );
}

function buildDynamicFightTimes(matches, tournament) {
  const result = new Map();
  const dayOneStart = getRingStartDate(tournament);

  if (!dayOneStart || !Array.isArray(matches) || matches.length === 0) {
    return result;
  }

  const sorted = [...matches].sort(
    (a, b) => Number(a.fight_number) - Number(b.fight_number)
  );

  // Day 1 contains every stage through the LAST SEMIFINAL.
  // There is intentionally NO fixed 18:00 cutoff.
  const dayOneFights = sorted.filter((match) => !isFinalFight(match));

  // Day 2 starts fresh at 09:00 and contains FINALS ONLY.
  const finalFights = sorted.filter((match) => isFinalFight(match));
  const finalsDayStart = getFinalsDayStart(dayOneStart);

  applyDynamicTimesForPhase({
    matches: dayOneFights,
    phaseStart: dayOneStart,
    result,
  });

  applyDynamicTimesForPhase({
    matches: finalFights,
    phaseStart: finalsDayStart,
    result,
  });

  return result;
}

function formatEventDay(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EVENT_TIMEZONE,
    day: "2-digit",
    month: "short",
  })
    .format(date)
    .toUpperCase();
}

function normalizeSchedulePayload(data) {
  if (data?.rings && typeof data.rings === "object") {
    return Object.values(data.rings).flat();
  }

  if (Array.isArray(data)) return data;

  return data?.schedule || data?.matches || [];
}

function normalizeStatus(value) {
  return String(value || "pending").toLowerCase().trim();
}

function getAssetUrl(value, type = "clubs") {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^https?:\/\//i.test(raw)) return raw;

  const clean = raw.replace(/^\/+/, "");

  if (clean.startsWith("api/")) {
    return `${WARFC_BASE}/${clean}`;
  }

  if (clean.startsWith("uploads/")) {
    return `${WARFC_BASE}/api/${clean}`;
  }

  return `${WARFC_BASE}/api/uploads/${type}/${clean}`;
}

function formatTournamentDate(start, end) {
  if (!start) return "";

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;

  if (Number.isNaN(startDate.getTime())) return "";

  const formatter = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  if (!endDate || Number.isNaN(endDate.getTime())) {
    return formatter.format(startDate);
  }

  const sameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  if (sameDay) return formatter.format(startDate);

  return `${formatter.format(startDate)} — ${formatter.format(endDate)}`;
}

function titleCase(value = "") {
  return String(value)
    .trim()
    .toLocaleLowerCase()
    .replace(/(^|\s|-)(\S)/g, (_, prefix, letter) =>
      `${prefix}${letter.toLocaleUpperCase()}`
    );
}

function formatFighterName(name = "", surname = "") {
  const cleanName = titleCase(name);
  const cleanSurname = String(surname || "").trim();

  if (!cleanName && !cleanSurname) return "TBD";

  // Preferred WAR FC display: surname initial first, then given name.
  // Example: Batdorj + Byambadorj -> B. Byambadorj
  if (cleanSurname) {
    return `${cleanSurname.charAt(0).toLocaleUpperCase()}. ${cleanName}`.trim();
  }

  // Fallback for APIs that may return a full name in one field.
  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    const first = parts.shift();
    return `${first.charAt(0).toLocaleUpperCase()}. ${parts.join(" ")}`;
  }

  return cleanName || "TBD";
}

function getRoundLabel(match) {
  const backendLabel = String(match?.round_label || "").trim();
  if (backendLabel) return backendLabel.toUpperCase();

  return `ROUND ${match?.round || "-"}`;
}

function getDivisionTypeLabel(value) {
  const raw = String(value || "").trim();
  const normalized = raw
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!normalized) return "ҮНДСЭН";

  if (
    normalized.includes("altan") ||
    normalized.includes("алтан")
  ) {
    return "АЛТАН ГАРАА";
  }

  if (
    normalized === "main" ||
    normalized.includes("undsen") ||
    normalized.includes("үндсэн")
  ) {
    return "ҮНДСЭН";
  }

  return raw.toUpperCase();
}

function getDivisionTypeTone(value) {
  return getDivisionTypeLabel(value) === "АЛТАН ГАРАА"
    ? "border-amber-300/30 bg-amber-300/10 text-amber-200"
    : "border-cyan-300/25 bg-cyan-300/[0.08] text-cyan-200";
}

function isFemaleFight(value) {
  const gender = String(value || "").toLowerCase().trim();

  return (
    gender === "f" ||
    gender === "female" ||
    gender.includes("female") ||
    gender === "woman" ||
    gender === "girl" ||
    gender === "эм" ||
    gender.includes("эмэгтэй")
  );
}

function getCorner(match, side) {
  const isRed = side === "red";

  const fighterId = isRed ? match.red_fighter : match.blue_fighter;
  const rawName = isRed ? match.red_fighter_name : match.blue_fighter_name;
  const surname = isRed
    ? match.red_fighter_surname ??
      match.red_surname ??
      match.red_fighter_last_name ??
      match.red_last_name
    : match.blue_fighter_surname ??
      match.blue_surname ??
      match.blue_fighter_last_name ??
      match.blue_last_name;
  const clubName = isRed ? match.red_club_name : match.blue_club_name;
  const clubLogo = isRed ? match.red_club_logo : match.blue_club_logo;
  const fighterImage = isRed
    ? match.red_fighter_image
    : match.blue_fighter_image;
  const sourceFight = isRed
    ? match.source_fights?.red
    : match.source_fights?.blue;

  if (fighterId || rawName) {
    return {
      type: "fighter",
      fighterId,
      name: formatFighterName(rawName || "Fighter", surname),
      rawName: rawName || "Fighter",
      surname: surname || "",
      clubName: clubName || "",
      clubLogo: getAssetUrl(clubLogo, "clubs"),
      fighterImage: getAssetUrl(fighterImage, "fighters"),
      sourceFight: null,
    };
  }

  if (Number(sourceFight) > 0) {
    return {
      type: "winner",
      fighterId: null,
      name: `WINNER OF FIGHT ${sourceFight}`,
      clubName: "",
      clubLogo: null,
      fighterImage: null,
      sourceFight: Number(sourceFight),
    };
  }

  return {
    type: "tbd",
    fighterId: null,
    name: "TBD",
    clubName: "",
    clubLogo: null,
    fighterImage: null,
    sourceFight: null,
  };
}

function StatusBadge({ completed, isNext }) {
  if (completed) {
    return (
      <span className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-emerald-200 sm:px-3 sm:text-[10px]">
        ✓ DONE
      </span>
    );
  }

  if (isNext) {
    return (
      <span className="rounded-full border border-amber-200/40 bg-amber-300 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-950 shadow-[0_0_18px_rgba(251,191,36,0.24)] sm:px-3 sm:text-[10px]">
        NEXT
      </span>
    );
  }

  return (
    <span className="rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-300 sm:px-3 sm:text-[10px]">
      UPCOMING
    </span>
  );
}

function CornerSide({ corner, side, onSourceFightClick, isWinner = false, isLoser = false }) {
  const isRed = side === "red";

  const accent = isRed
    ? "border-l-4 border-l-red-500 bg-gradient-to-r from-red-600/25 via-red-500/[0.08] to-transparent shadow-[inset_10px_0_22px_rgba(239,68,68,0.12)]"
    : "border-r-4 border-r-blue-500 bg-gradient-to-l from-blue-600/25 via-blue-500/[0.08] to-transparent shadow-[inset_-10px_0_22px_rgba(59,130,246,0.12)]";

  const nameTone = isRed ? "text-red-100" : "text-blue-100";
  const clubTone = isRed ? "text-red-200/70" : "text-blue-200/70";
  const sourceTone = isRed ? "text-red-100" : "text-blue-100";

  const resultTone = isWinner
    ? "opacity-100 brightness-110"
    : isLoser
    ? "opacity-[0.38] saturate-[0.55]"
    : "opacity-100";

  const content =
    corner.type === "winner" ? (
      <button
        type="button"
        onClick={() => onSourceFightClick(corner.sourceFight)}
        className={`relative z-10 min-w-0 flex-1 ${
          isRed ? "text-left" : "text-right"
        }`}
      >
        <div
          className={`text-[10px] font-black uppercase leading-tight tracking-[0.06em] sm:text-xs ${sourceTone}`}
        >
          Winner of Fight {corner.sourceFight}
        </div>
        <div className="mt-1 hidden text-[9px] font-semibold text-slate-400 sm:block">
          Tap to view source fight
        </div>
      </button>
    ) : corner.type === "tbd" ? (
      <div
        className={`relative z-10 min-w-0 flex-1 ${
          isRed ? "text-left" : "text-right"
        }`}
      >
        <div className="text-xs font-black tracking-[0.04em] text-slate-400 sm:text-sm">
          TBD
        </div>
      </div>
    ) : (
      <div
        className={`relative z-10 min-w-0 flex-1 ${
          isRed ? "text-left" : "text-right"
        }`}
      >
        {corner.fighterId ? (
          <Link
            to={`/fighters/${corner.fighterId}`}
            className={`block truncate text-[14px] font-black uppercase leading-[1.02] tracking-[-0.02em] transition hover:brightness-125 hover:underline hover:underline-offset-4 sm:text-[18px] lg:text-[20px] ${nameTone}`}
          >
            {corner.name}
          </Link>
        ) : (
          <div
            className={`truncate text-[14px] font-black uppercase leading-[1.02] tracking-[-0.02em] sm:text-[18px] lg:text-[20px] ${nameTone}`}
          >
            {corner.name}
          </div>
        )}

        {corner.clubName && (
          <div
            className={`mt-1.5 truncate text-[8px] font-bold uppercase tracking-[0.12em] sm:text-[10px] ${clubTone}`}
          >
            {corner.clubName}
          </div>
        )}

        {isWinner && corner.type === "fighter" && (
  <div
    className="
      mt-2 inline-flex items-center
      rounded-full
      border border-amber-300/50
      bg-amber-300 text-black border-amber-200
      px-2.5 py-1
      text-[8px] font-black uppercase
      tracking-[0.14em]
      text-amber-200
      shadow-[0_0_16px_rgba(251,191,36,0.12)]
      sm:text-[9px]
    "
  >
    ✓ Winner
  </div>
)}
      </div>
    );

  return (
    <div className={`relative min-w-0 overflow-hidden transition-all duration-300 ${accent} ${resultTone}`}>
      {/* Club logo as a soft watermark background */}
      {corner.clubLogo && (
        <img
          src={corner.clubLogo}
          alt=""
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 h-20 w-20 -translate-y-1/2 object-contain opacity-[0.10] grayscale-[0.10] sm:h-24 sm:w-24 ${
            isRed ? "right-2" : "left-2"
          }`}
        />
      )}

      <div
        className={`relative flex min-h-[84px] min-w-0 items-center px-3 py-4 sm:min-h-[96px] sm:px-5 ${
          isRed ? "justify-end pr-4 sm:pr-6" : "justify-start pl-4 sm:pl-6"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n\r]/.test(text)
    ? `"${text.replace(/"/g, '""')}"`
    : text;
}

function safeFileName(value = "") {
  return String(value || "WARFC")
    .trim()
    .replace(/[^\p{L}\p{N}_-]+/gu, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "WARFC";
}

export default function PublicFightSchedule() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [schedule, setSchedule] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeRing, setActiveRing] = useState(1);
  const [search, setSearch] = useState("");
  const [highlightedFight, setHighlightedFight] = useState(null);

  const [moveAgeDivisionId, setMoveAgeDivisionId] = useState("");
  const [moveType, setMoveType] = useState("__ALL__");
  const [moveTargetRing, setMoveTargetRing] = useState(2);
  const [movingAgeDivision, setMovingAgeDivision] = useState(false);

  const mountedRef = useRef(true);
  const highlightTimerRef = useRef(null);

  const loadTournament = useCallback(async () => {
    try {
      const data = await API.get(`/tournaments/${id}`);

      if (mountedRef.current) {
        setTournament(data?.tournament || data?.data || data);
      }
    } catch (err) {
      console.error("Failed to load tournament:", err);
    }
  }, [id]);

  const loadSchedule = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const data = await API.get(`/tournaments/${id}/fight-schedule`);

        const matches = normalizeSchedulePayload(data)
          .filter((match) => Number(match.fight_number) > 0)
          .sort((a, b) => {
            const ringDiff =
              Number(a.ring_number || 1) - Number(b.ring_number || 1);

            if (ringDiff !== 0) return ringDiff;

            return Number(a.fight_number) - Number(b.fight_number);
          });

        if (!mountedRef.current) return;

        setSchedule(matches);
        setError("");

        const availableRings = [
          ...new Set(
            matches
              .map((match) => Number(match.ring_number || 1))
              .filter(Boolean)
          ),
        ].sort((a, b) => a - b);

        if (availableRings.length > 0) {
          setActiveRing((current) =>
            availableRings.includes(Number(current))
              ? Number(current)
              : availableRings[0]
          );
        }
      } catch (err) {
        console.error("Failed to load public fight schedule:", err);

        if (mountedRef.current && !silent) {
          setError(err?.message || "Failed to load fight schedule.");
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [id]
  );

  useEffect(() => {
    mountedRef.current = true;

    loadTournament();
    loadSchedule();

    const interval = window.setInterval(() => {
      loadSchedule({ silent: true });
    }, REFRESH_MS);

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        loadSchedule({ silent: true });
      }
    };

    const refreshOnFocus = () => {
      loadSchedule({ silent: true });
    };

    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshOnFocus);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshOnFocus);

      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, [loadSchedule, loadTournament]);

  useEffect(() => {
    if (tournament?.name) {
      document.title = `${tournament.name} • Оноолт • WAR FC`;
    }
  }, [tournament]);

  const rings = useMemo(
    () =>
      [
        ...new Set(
          schedule
            .map((match) => Number(match.ring_number || 1))
            .filter(Boolean)
        ),
      ].sort((a, b) => a - b),
    [schedule]
  );

  const activeRingMatches = useMemo(
    () =>
      schedule
        .filter(
          (match) =>
            Number(match.ring_number || 1) === Number(activeRing)
        )
        .sort(
          (a, b) => Number(a.fight_number) - Number(b.fight_number)
        ),
    [schedule, activeRing]
  );


  const liveClubStandings = useMemo(
    () => buildLiveClubStandings(schedule, "main"),
    [schedule]
  );

  const liveAltanGaraaStandings = useMemo(
    () => buildLiveClubStandings(schedule, "altan_garaa"),
    [schedule]
  );

  const filteredMatches = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return activeRingMatches;

    return activeRingMatches.filter((match) => {
      const haystack = [
        `#${match.fight_number}`,
        match.fight_number,
        match.red_fighter_name,
        match.red_fighter_surname,
        match.red_surname,
        match.blue_fighter_name,
        match.blue_fighter_surname,
        match.blue_surname,
        match.red_club_name,
        match.blue_club_name,
        match.age_division,
        match.weightclass_name,
        match.type,
        match.round_label,
        match.round,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [activeRingMatches, search]);

  const completedCount = useMemo(
    () =>
      activeRingMatches.filter(
        (match) => normalizeStatus(match.status) === "completed"
      ).length,
    [activeRingMatches]
  );

  const nextFight = useMemo(
    () =>
      activeRingMatches.find(
        (match) => normalizeStatus(match.status) !== "completed"
      ) || null,
    [activeRingMatches]
  );

  const dayOneStart = getRingStartDate(tournament);
  const finalsDayStart = getFinalsDayStart(dayOneStart);
  const finalsDayLabel = finalsDayStart
    ? formatEventDay(finalsDayStart)
    : "DAY 2";

  const fightTimes = useMemo(
    () => buildDynamicFightTimes(activeRingMatches, tournament),
    [activeRingMatches, tournament]
  );

  const nextFightTime = nextFight
    ? fightTimes.get(getMatchTimeKey(nextFight))
    : null;

  const estimatedRingFinish = useMemo(() => {
    if (!activeRingMatches.length) return null;

    const lastMatch =
      activeRingMatches[activeRingMatches.length - 1];
    const lastTime = fightTimes.get(getMatchTimeKey(lastMatch));

    if (!lastTime?.time) return null;

    if (lastTime.kind === "actual") {
      return lastTime.time;
    }

    return new Date(
      lastTime.time.getTime() +
        getExpectedBoutMinutes(lastMatch.age_division) * 60 * 1000
    );
  }, [activeRingMatches, fightTimes]);

  const scrollToFight = useCallback(
    (fightNumber) => {
      const fight = activeRingMatches.find(
        (match) => Number(match.fight_number) === Number(fightNumber)
      );

      if (!fight) return;

      setHighlightedFight(Number(fightNumber));

      window.requestAnimationFrame(() => {
        const element = document.getElementById(
          `fight-${Number(activeRing)}-${Number(fightNumber)}`
        );

        element?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });

      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }

      highlightTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) {
          setHighlightedFight(null);
        }
      }, 2500);
    },
    [activeRing, activeRingMatches]
  );

  const ageDivisionOptions = useMemo(() => {
    const map = new Map();

    for (const match of schedule) {
      const ageDivisionId = Number(match?.age_division_id);
      const ageDivision = String(match?.age_division || "").trim();

      if (!Number.isInteger(ageDivisionId) || ageDivisionId <= 0 || !ageDivision) {
        continue;
      }

      if (!map.has(ageDivisionId)) {
        map.set(ageDivisionId, {
          id: ageDivisionId,
          name: ageDivision,
        });
      }
    }

    return [...map.values()].sort((a, b) => {
      const aNumber = Number(String(a.name).match(/\d+/)?.[0] || 999);
      const bNumber = Number(String(b.name).match(/\d+/)?.[0] || 999);

      if (aNumber !== bNumber) return aNumber - bNumber;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [schedule]);

  const selectedAgeDivision = useMemo(
    () =>
      ageDivisionOptions.find(
        (item) => Number(item.id) === Number(moveAgeDivisionId)
      ) || null,
    [ageDivisionOptions, moveAgeDivisionId]
  );

  const selectedAgeCurrentRings = useMemo(() => {
    if (!moveAgeDivisionId) return [];

    return [
      ...new Set(
        schedule
          .filter(
            (match) =>
              Number(match?.age_division_id) === Number(moveAgeDivisionId)
          )
          .map((match) => Number(match?.ring_number || 1))
          .filter(Boolean)
      ),
    ].sort((a, b) => a - b);
  }, [schedule, moveAgeDivisionId]);

  const selectedAgeIsSplit = selectedAgeCurrentRings.length > 1;

  const selectedAgeTypeRings = useMemo(() => {
    if (!moveAgeDivisionId) return [];

    const map = new Map();

    for (const match of schedule) {
      if (
        Number(match?.age_division_id) !== Number(moveAgeDivisionId)
      ) {
        continue;
      }

      const rawType = String(match?.type || "").trim();
      const typeKey = rawType || "__MAIN_FALLBACK__";
      const typeLabel = getDivisionTypeLabel(rawType);
      const ringNumber = Number(match?.ring_number || 1);

      if (!map.has(typeKey)) {
        map.set(typeKey, {
          rawType,
          label: typeLabel,
          rings: new Set(),
        });
      }

      map.get(typeKey).rings.add(ringNumber);
    }

    const preferredOrder = {
      "ҮНДСЭН": 1,
      "АЛТАН ГАРАА": 2,
    };

    return [...map.values()]
      .map((item) => ({
        rawType: item.rawType,
        label: item.label,
        rings: [...item.rings].sort((a, b) => a - b),
      }))
      .sort(
        (a, b) =>
          (preferredOrder[a.label] || 99) -
            (preferredOrder[b.label] || 99) ||
          a.label.localeCompare(b.label)
      );
  }, [schedule, moveAgeDivisionId]);

  const selectedMoveTypeOption = useMemo(() => {
    if (moveType === "__ALL__") {
      return {
        rawType: "__ALL__",
        label: "ҮНДСЭН + АЛТАН ГАРАА",
        rings: selectedAgeCurrentRings,
      };
    }

    return (
      selectedAgeTypeRings.find(
        (item) => String(item.rawType) === String(moveType)
      ) || null
    );
  }, [
    moveType,
    selectedAgeCurrentRings,
    selectedAgeTypeRings,
  ]);

  const selectedMoveCurrentRings =
    selectedMoveTypeOption?.rings || [];

  const selectedAgeAlreadyOnTarget =
    selectedMoveCurrentRings.length === 1 &&
    Number(selectedMoveCurrentRings[0]) === Number(moveTargetRing);

  const moveEntireAgeDivision = useCallback(async () => {
    if (!isAdmin || movingAgeDivision) return;

    const ageDivisionId = Number(moveAgeDivisionId);
    const targetRing = Number(moveTargetRing);

    if (!Number.isInteger(ageDivisionId) || ageDivisionId <= 0) {
      window.alert("Age division сонгоно уу.");
      return;
    }

    if (![1, 2].includes(targetRing)) {
      window.alert("Ring 1 эсвэл Ring 2 сонгоно уу.");
      return;
    }

    if (!selectedMoveTypeOption) {
      window.alert("ҮНДСЭН, АЛТАН ГАРАА эсвэл BOTH сонгоно уу.");
      return;
    }

    if (
      selectedMoveCurrentRings.length === 1 &&
      Number(selectedMoveCurrentRings[0]) === targetRing
    ) {
      window.alert(
        `${selectedAgeDivision?.name || "This age division"} • ` +
          `${selectedMoveTypeOption.label} already belongs completely to Ring ${targetRing}.`
      );
      return;
    }

    const ageName =
      selectedAgeDivision?.name || `Age division ${ageDivisionId}`;

    const typeLabel = selectedMoveTypeOption.label;

    const currentRingText = selectedMoveCurrentRings.length
      ? selectedMoveCurrentRings
          .map((ring) => `Ring ${ring}`)
          .join(", ")
      : "unknown";

    const bothTypes = moveType === "__ALL__";

    const confirmed = window.confirm(
      `${ageName} • ${typeLabel}\n` +
        `Ring ${targetRing} рүү шилжүүлэх үү?\n\n` +
        `Одоогийн байрлал: ${currentRingText}\n\n` +
        (bothTypes
          ? `ҮНДСЭН + АЛТАН ГАРАА хоёулаа шилжинэ.\n`
          : `${typeLabel} ТӨРӨЛ Л шилжинэ.\n`) +
        `Эрэгтэй + эмэгтэй + бүх жин + бүх үе шат хамт шилжинэ.\n` +
        `Дараа нь Ring 1 / Ring 2 хуваарийг автоматаар дахин үүсгэнэ.\n\n` +
        `Сонгосон хэсэгт бодит дууссан тулаан байгаа бол систем шилжүүлэхийг хориглоно.`
    );

    if (!confirmed) return;

    setMovingAgeDivision(true);

    try {
      const moveResult = await API.put(
        `/tournaments/${id}/fight-schedule/move-age-division`,
        {
          age_division_id: ageDivisionId,
          type: moveType,
          target_ring: targetRing,
        }
      );

      await API.post(
        `/tournaments/${id}/generate-fight-schedule`,
        {}
      );

      await loadSchedule({ silent: true });

      setActiveRing(targetRing);
      setSearch("");

      window.alert(
        moveResult?.message ||
          `${ageName} • ${typeLabel} Ring ${targetRing} рүү шилжиж, хуваарь дахин үүслээ.`
      );
    } catch (err) {
      console.error("Failed to move age division/type:", err);

      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Age division/type шилжүүлэхэд алдаа гарлаа."
      );

      await loadSchedule({ silent: true });
    } finally {
      if (mountedRef.current) {
        setMovingAgeDivision(false);
      }
    }
  }, [
    id,
    isAdmin,
    loadSchedule,
    moveAgeDivisionId,
    moveTargetRing,
    moveType,
    movingAgeDivision,
    selectedAgeDivision,
    selectedMoveCurrentRings,
    selectedMoveTypeOption,
  ]);

  const downloadRingSchedule = useCallback(
    (ringNumber) => {
      if (!isAdmin) return;

      const ring = Number(ringNumber);
      const ringMatches = schedule
        .filter((match) => Number(match?.ring_number || 1) === ring)
        .filter((match) => Number(match?.fight_number) > 0)
        .sort((a, b) =>
          Number(a?.fight_number || 0) - Number(b?.fight_number || 0)
        );

      if (!ringMatches.length) {
        window.alert(`Ring ${ring} дээр татах тулаан алга.`);
        return;
      }

      const headers = [
        "ring_number",
        "fight_number",
        "match_id",
        "bracket_id",
        "round",
        "round_label",
        "age_division",
        "weightclass_name",
        "division_weightclass_id",
        "status",
        "red_fighter_id",
        "red_fighter_name",
        "red_fighter_surname",
        "red_club_id",
        "red_club_name",
        "red_source_fight",
        "blue_fighter_id",
        "blue_fighter_name",
        "blue_fighter_surname",
        "blue_club_id",
        "blue_club_name",
        "blue_source_fight",
      ];

      const rows = ringMatches.map((match) => ({
        ring_number: ring,
        fight_number: match?.fight_number ?? "",
        match_id: match?.id ?? match?.match_id ?? "",
        bracket_id: match?.bracket_id ?? "",
        round: match?.round ?? "",
        round_label: match?.round_label ?? "",
        age_division: match?.age_division ?? "",
        weightclass_name: match?.weightclass_name ?? "",
        division_weightclass_id: match?.division_weightclass_id ?? "",
        status: match?.status ?? "pending",
        red_fighter_id: match?.red_fighter ?? "",
        red_fighter_name: match?.red_fighter_name ?? "",
        red_fighter_surname:
          match?.red_fighter_surname ??
          match?.red_surname ??
          match?.red_fighter_last_name ??
          "",
        red_club_id:
          match?.red_club_id ?? match?.red_fighter_club_id ?? "",
        red_club_name: match?.red_club_name ?? "",
        red_source_fight: match?.source_fights?.red ?? "",
        blue_fighter_id: match?.blue_fighter ?? "",
        blue_fighter_name: match?.blue_fighter_name ?? "",
        blue_fighter_surname:
          match?.blue_fighter_surname ??
          match?.blue_surname ??
          match?.blue_fighter_last_name ??
          "",
        blue_club_id:
          match?.blue_club_id ?? match?.blue_fighter_club_id ?? "",
        blue_club_name: match?.blue_club_name ?? "",
        blue_source_fight: match?.source_fights?.blue ?? "",
      }));

      const csv = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) =>
          headers.map((header) => csvEscape(row[header])).join(",")
        ),
      ].join("\r\n");

      const blob = new Blob(["\uFEFF", csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const tournamentName = safeFileName(
        tournament?.name || `tournament_${id}`
      );

      link.href = url;
      link.download = `${tournamentName}_RING_${ring}_FIGHT_SCHEDULE.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    [id, isAdmin, schedule, tournament]
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.065),transparent_28%),radial-gradient(circle_at_top_right,rgba(244,63,94,0.06),transparent_28%),linear-gradient(180deg,#070b12_0%,#09101a_38%,#070b12_100%)] text-white">

      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:py-8">
        <section className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0d141f]/92 px-4 py-4 shadow-[0_20px_55px_rgba(0,0,0,0.28)] sm:px-6 sm:py-5">
          <div className="pointer-events-none absolute -left-16 top-0 h-40 w-40 rounded-full bg-cyan-400/[0.07] blur-3xl" />
          <div className="pointer-events-none absolute -right-16 top-0 h-40 w-40 rounded-full bg-rose-400/[0.07] blur-3xl" />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="text-[9px] font-black uppercase tracking-[0.28em] text-cyan-200/70 sm:text-[10px]">
                WAR FC • FIGHT SCHEDULE
              </div>
              <h1 className="mt-1.5 truncate text-xl font-black uppercase tracking-tight text-white sm:text-2xl lg:text-3xl">
                {tournament?.name || "Тулааны оноолт"}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[10px] font-black uppercase tracking-[0.08em] text-slate-400 sm:text-[11px]">
                <span className="text-white">Ring {activeRing}</span>
                <span className="text-slate-600">•</span>
                <span>
                  {completedCount}/{activeRingMatches.length} fights
                </span>
                {estimatedRingFinish && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-cyan-200/75">
                      End {formatEventTime(estimatedRingFinish)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {isAdmin && rings.length > 0 && (
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {rings.map((ring) => (
                  <button
                    key={`download-ring-${ring}`}
                    type="button"
                    onClick={() => downloadRingSchedule(ring)}
                    className="rounded-lg border border-amber-300/25 bg-amber-300/10 px-3.5 py-2.5 text-[10px] font-black uppercase tracking-[0.10em] text-amber-200 transition hover:border-amber-300/45 hover:bg-amber-300/15 active:scale-[0.98]"
                  >
                    ↓ Ring {ring} CSV
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {isAdmin && rings.length >= 2 && (
          <section className="mt-4 overflow-hidden rounded-2xl border border-amber-300/20 bg-gradient-to-r from-amber-300/[0.055] via-[#0d141f] to-cyan-300/[0.045] shadow-[0_14px_34px_rgba(0,0,0,0.22)]">
            <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 lg:flex-row lg:items-end">
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-black uppercase tracking-[0.26em] text-amber-200/75">
                  ADMIN • RING BALANCE
                </div>
                <div className="mt-1 text-sm font-black uppercase tracking-[0.04em] text-white sm:text-base">
                  Move Age Division / Type
                </div>
                <div className="mt-1 text-[10px] font-semibold text-slate-500 sm:text-[11px]">
                  Move ҮНДСЭН, АЛТАН ГАРАА, or BOTH. Male + Female + All Weights + All Rounds stay together inside the selected type.
                </div>
              </div>

              <div className="grid w-full gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(150px,1fr)_170px_110px_auto] lg:w-auto">
                <label className="block">
                  <span className="mb-1 block text-[8px] font-black uppercase tracking-[0.18em] text-slate-500">
                    Age Division
                  </span>
                  <select
                    value={moveAgeDivisionId}
                    onChange={(event) => {
                      setMoveAgeDivisionId(event.target.value);
                      setMoveType("__ALL__");
                    }}
                    disabled={movingAgeDivision}
                    className="h-11 w-full rounded-lg border border-white/[0.10] bg-[#080d15] px-3 text-xs font-black uppercase text-white outline-none transition focus:border-amber-300/50 disabled:opacity-50"
                  >
                    <option value="">SELECT AGE</option>
                    {ageDivisionOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[8px] font-black uppercase tracking-[0.18em] text-slate-500">
                    Type
                  </span>
                  <select
                    value={moveType}
                    onChange={(event) => setMoveType(event.target.value)}
                    disabled={movingAgeDivision || !moveAgeDivisionId}
                    className="h-11 w-full rounded-lg border border-white/[0.10] bg-[#080d15] px-3 text-xs font-black uppercase text-white outline-none transition focus:border-amber-300/50 disabled:opacity-50"
                  >
                    <option value="__ALL__">BOTH</option>
                    {selectedAgeTypeRings.map((item) => (
                      <option
                        key={item.rawType || item.label}
                        value={item.rawType}
                      >
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[8px] font-black uppercase tracking-[0.18em] text-slate-500">
                    Move To
                  </span>
                  <select
                    value={moveTargetRing}
                    onChange={(event) => setMoveTargetRing(Number(event.target.value))}
                    disabled={movingAgeDivision}
                    className="h-11 w-full rounded-lg border border-white/[0.10] bg-[#080d15] px-3 text-xs font-black uppercase text-white outline-none transition focus:border-cyan-300/50 disabled:opacity-50"
                  >
                    <option value={1}>RING 1</option>
                    <option value={2}>RING 2</option>
                  </select>
                </label>

                <button
                  type="button"
                  onClick={moveEntireAgeDivision}
                  disabled={
                    !moveAgeDivisionId ||
                    movingAgeDivision ||
                    selectedAgeAlreadyOnTarget
                  }
                  className={`h-11 self-end rounded-lg px-4 text-[10px] font-black uppercase tracking-[0.10em] transition active:scale-[0.98] ${
                    moveAgeDivisionId &&
                    !movingAgeDivision &&
                    !selectedAgeAlreadyOnTarget
                      ? "border border-amber-300/40 bg-amber-300 text-slate-950 shadow-[0_8px_22px_rgba(251,191,36,0.16)] hover:bg-amber-200"
                      : "cursor-not-allowed border border-white/[0.07] bg-white/[0.03] text-slate-600"
                  }`}
                >
                  {movingAgeDivision
                    ? "MOVING..."
                    : selectedAgeAlreadyOnTarget
                    ? `ALREADY ON RING ${moveTargetRing}`
                    : `MOVE → RING ${moveTargetRing}`}
                </button>
              </div>
            </div>

            {selectedAgeDivision && (
              <div className="border-t border-white/[0.06] bg-black/15 px-4 py-2.5 sm:px-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-[9px] font-black uppercase tracking-[0.12em] text-white">
                    {selectedAgeDivision.name}
                  </span>

                  {selectedAgeTypeRings.map((item) => (
                    <span
                      key={item.rawType || item.label}
                      className={`rounded-md border px-2 py-1 text-[8px] font-black uppercase tracking-[0.10em] ${
                        item.label === "АЛТАН ГАРАА"
                          ? "border-amber-300/25 bg-amber-300/[0.08] text-amber-200"
                          : "border-cyan-300/20 bg-cyan-300/[0.06] text-cyan-200"
                      }`}
                    >
                      {item.label}:{" "}
                      {item.rings.map((ring) => `RING ${ring}`).join(" + ")}
                    </span>
                  ))}

                  <span className="text-[8px] font-bold uppercase tracking-[0.10em] text-slate-600">
                    Select ҮНДСЭН or АЛТАН ГАРАА to move separately. BOTH moves both types.
                  </span>
                </div>
              </div>
            )}
          </section>
        )}

        {error && (
          <div className="mt-4 rounded-xl border border-rose-300/25 bg-rose-400/[0.08] p-4 text-sm font-bold text-rose-100">
            {error}
          </div>
        )}

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <section className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0d141f]/95 shadow-[0_16px_38px_rgba(0,0,0,0.24)]">
            <div className="flex items-center justify-between gap-4 border-b border-white/[0.07] bg-white/[0.018] px-4 py-3 sm:px-5">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.26em] text-cyan-200/75">
                  LIVE CLUB STANDINGS • ҮНДСЭН
                </div>
                <div className="mt-1 text-[9px] font-semibold text-slate-500">
                  АЛТАН ГАРАА медаль орохгүй
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 rounded-full border border-emerald-300/15 bg-emerald-400/[0.06] px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                <span className="text-[8px] font-black uppercase tracking-[0.16em] text-emerald-200/80 sm:text-[9px]">
                  LIVE
                </span>
              </div>
            </div>

            <div className="grid grid-cols-[34px_minmax(0,1fr)_38px_38px_38px_52px] items-center border-b border-white/[0.06] bg-black/20 px-3 py-2 text-[8px] font-black uppercase tracking-[0.14em] text-slate-500 sm:grid-cols-[44px_minmax(0,1fr)_52px_52px_52px_68px] sm:px-5 sm:text-[9px]">
              <div>#</div>
              <div>Club</div>
              <div className="text-center text-amber-300/80">G</div>
              <div className="text-center text-slate-300/80">S</div>
              <div className="text-center text-orange-300/80">B</div>
              <div className="text-center">Total</div>
            </div>

            {liveClubStandings.length === 0 ? (
              <div className="px-4 py-8 text-center sm:px-5">
                <div className="text-sm font-black uppercase tracking-[0.08em] text-slate-400">
                  Үндсэн медалийн дүн хараахан гараагүй байна
                </div>
                <div className="mt-1 text-[10px] font-semibold text-slate-600 sm:text-xs">
                  ҮНДСЭН төрлийн semifinal болон final дуусахад автоматаар шинэчлэгдэнэ.
                </div>
              </div>
            ) : (
              <>
                <div className="max-h-[240px] overflow-y-auto overscroll-contain">
                  {liveClubStandings.map((club, index) => (
                    <div
                      key={club.club_id || club.club_name}
                      className={`grid min-h-[48px] grid-cols-[34px_minmax(0,1fr)_38px_38px_38px_52px] items-center border-b border-white/[0.05] px-3 transition last:border-b-0 sm:grid-cols-[44px_minmax(0,1fr)_52px_52px_52px_68px] sm:px-5 ${
                        index < 3 ? "bg-white/[0.018]" : ""
                      }`}
                    >
                      <div
                        className={`text-sm font-black tabular-nums ${
                          index === 0
                            ? "text-amber-300"
                            : index === 1
                            ? "text-slate-200"
                            : index === 2
                            ? "text-orange-300"
                            : "text-slate-500"
                        }`}
                      >
                        {index + 1}
                      </div>

                      <div className="flex min-w-0 items-center gap-2.5 pr-2">
                        {club.club_logo ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-black/25 p-1">
                            <img
                              src={club.club_logo}
                              alt=""
                              aria-hidden="true"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.025] text-[9px] font-black text-slate-600">
                            W
                          </div>
                        )}

                        <div className="truncate text-[11px] font-black uppercase tracking-[0.02em] text-white sm:text-xs">
                          {club.club_name}
                        </div>
                      </div>

                      <div className="text-center text-sm font-black tabular-nums text-amber-300">
                        {club.gold}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-slate-200">
                        {club.silver}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-orange-300">
                        {club.bronze}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-white">
                        {club.total}
                      </div>
                    </div>
                  ))}
                </div>

                {liveClubStandings.length > 8 && (
                  <div className="border-t border-white/[0.06] bg-black/15 px-4 py-2 text-center text-[8px] font-black uppercase tracking-[0.16em] text-slate-600 sm:text-[9px]">
                    Нийт {liveClubStandings.length} клуб • Доош гүйлгэж үзнэ үү
                  </div>
                )}
              </>
            )}
          </section>

          <section className="overflow-hidden rounded-2xl border border-amber-300/15 bg-[#0d141f]/95 shadow-[0_16px_38px_rgba(0,0,0,0.24)]">
            <div className="flex items-center justify-between gap-4 border-b border-amber-300/[0.10] bg-amber-300/[0.025] px-4 py-3 sm:px-5">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.26em] text-amber-200/80">
                  АЛТАН ГАРАА STANDINGS
                </div>
                <div className="mt-1 text-[9px] font-semibold text-slate-500">
                  Зөвхөн АЛТАН ГАРАА медалийн тоо
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 rounded-full border border-emerald-300/15 bg-emerald-400/[0.06] px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                <span className="text-[8px] font-black uppercase tracking-[0.16em] text-emerald-200/80 sm:text-[9px]">
                  LIVE
                </span>
              </div>
            </div>

            <div className="grid grid-cols-[34px_minmax(0,1fr)_38px_38px_38px_52px] items-center border-b border-white/[0.06] bg-black/20 px-3 py-2 text-[8px] font-black uppercase tracking-[0.14em] text-slate-500 sm:grid-cols-[44px_minmax(0,1fr)_52px_52px_52px_68px] sm:px-5 sm:text-[9px]">
              <div>#</div>
              <div>Club</div>
              <div className="text-center text-amber-300/80">G</div>
              <div className="text-center text-slate-300/80">S</div>
              <div className="text-center text-orange-300/80">B</div>
              <div className="text-center">Total</div>
            </div>

            {liveAltanGaraaStandings.length === 0 ? (
              <div className="px-4 py-8 text-center sm:px-5">
                <div className="text-sm font-black uppercase tracking-[0.08em] text-slate-400">
                  Алтан Гараа медалийн дүн хараахан гараагүй байна
                </div>
                <div className="mt-1 text-[10px] font-semibold text-slate-600 sm:text-xs">
                  АЛТАН ГАРАА төрлийн semifinal болон final дуусахад автоматаар шинэчлэгдэнэ.
                </div>
              </div>
            ) : (
              <>
                <div className="max-h-[240px] overflow-y-auto overscroll-contain">
                  {liveAltanGaraaStandings.map((club, index) => (
                    <div
                      key={club.club_id || club.club_name}
                      className={`grid min-h-[48px] grid-cols-[34px_minmax(0,1fr)_38px_38px_38px_52px] items-center border-b border-white/[0.05] px-3 transition last:border-b-0 sm:grid-cols-[44px_minmax(0,1fr)_52px_52px_52px_68px] sm:px-5 ${
                        index < 3 ? "bg-amber-300/[0.018]" : ""
                      }`}
                    >
                      <div
                        className={`text-sm font-black tabular-nums ${
                          index === 0
                            ? "text-amber-300"
                            : index === 1
                            ? "text-slate-200"
                            : index === 2
                            ? "text-orange-300"
                            : "text-slate-500"
                        }`}
                      >
                        {index + 1}
                      </div>

                      <div className="flex min-w-0 items-center gap-2.5 pr-2">
                        {club.club_logo ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-black/25 p-1">
                            <img
                              src={club.club_logo}
                              alt=""
                              aria-hidden="true"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.025] text-[9px] font-black text-slate-600">
                            W
                          </div>
                        )}

                        <div className="truncate text-[11px] font-black uppercase tracking-[0.02em] text-white sm:text-xs">
                          {club.club_name}
                        </div>
                      </div>

                      <div className="text-center text-sm font-black tabular-nums text-amber-300">
                        {club.gold}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-slate-200">
                        {club.silver}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-orange-300">
                        {club.bronze}
                      </div>
                      <div className="text-center text-sm font-black tabular-nums text-white">
                        {club.total}
                      </div>
                    </div>
                  ))}
                </div>

                {liveAltanGaraaStandings.length > 8 && (
                  <div className="border-t border-white/[0.06] bg-black/15 px-4 py-2 text-center text-[8px] font-black uppercase tracking-[0.16em] text-slate-600 sm:text-[9px]">
                    Нийт {liveAltanGaraaStandings.length} клуб • Доош гүйлгэж үзнэ үү
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        <section className="sticky top-0 z-20 -mx-3 mt-4 border-y border-white/[0.07] bg-[#080d15]/94 px-3 py-3 shadow-[0_10px_28px_rgba(0,0,0,0.24)] backdrop-blur-2xl sm:-mx-6 sm:px-6">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
              {rings.map((ring) => {
                const count = schedule.filter(
                  (match) => Number(match.ring_number || 1) === ring
                ).length;

                return (
                  <button
                    key={ring}
                    type="button"
                    onClick={() => {
                      setActiveRing(ring);
                      setSearch("");
                    }}
                    className={`shrink-0 rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wide transition ${
                      Number(activeRing) === Number(ring)
                        ? "border border-cyan-300/40 bg-cyan-300 text-slate-950 shadow-[0_8px_22px_rgba(34,211,238,0.16)]"
                        : "border border-white/[0.08] bg-white/[0.035] text-slate-400 hover:bg-white/[0.06] hover:text-white"
                    }`}
                  >
                    Ring {ring}
                    <span className="ml-2 opacity-60">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="relative w-full lg:max-w-sm">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Тамирчин, клуб, тулааны дугаар хайх..."
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.035] px-4 py-2.5 pr-10 text-sm font-semibold text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300/40 focus:bg-white/[0.05]"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </section>

        {loading ? (
          <div className="py-24 text-center text-sm font-bold uppercase tracking-[0.2em] text-slate-600">
            Loading official schedule...
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-white/[0.08] bg-[#0d141f] p-12 text-center">
            <div className="text-4xl">🥊</div>
            <div className="mt-3 text-lg font-black uppercase">No fights found</div>
            <div className="mt-1 text-sm text-slate-500">
              Try another ring or search term.
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-2.5 sm:space-y-3">
            {filteredMatches.map((match, index) => {
              const red = getCorner(match, "red");
              const blue = getCorner(match, "blue");
              const completed = normalizeStatus(match.status) === "completed";

              const winnerId =
                completed && match.winner !== null && match.winner !== undefined
                  ? String(match.winner)
                  : null;

              const redIsWinner =
                Boolean(winnerId) &&
                String(match.red_fighter ?? "") === winnerId;

              const blueIsWinner =
                Boolean(winnerId) &&
                String(match.blue_fighter ?? "") === winnerId;

              const redIsLoser =
                completed &&
                Boolean(winnerId) &&
                Boolean(match.red_fighter) &&
                !redIsWinner;

              const blueIsLoser =
                completed &&
                Boolean(winnerId) &&
                Boolean(match.blue_fighter) &&
                !blueIsWinner;

              const isHighlighted =
                Number(highlightedFight) === Number(match.fight_number);
              const isNext =
                nextFight &&
                Number(nextFight.fight_number) === Number(match.fight_number);
              const timeInfo = fightTimes.get(getMatchTimeKey(match));
              const showFinalsDivider =
                isFinalFight(match) &&
                (index === 0 || !isFinalFight(filteredMatches[index - 1]));

              return (
                <React.Fragment key={match.id}>
                  {showFinalsDivider && (
                    <div className="my-5 overflow-hidden rounded-xl border border-amber-200/20 bg-gradient-to-r from-amber-300/[0.06] via-white/[0.025] to-cyan-300/[0.05] px-4 py-3 text-center shadow-[0_10px_28px_rgba(0,0,0,0.20)]">
                      <div className="text-[9px] font-black uppercase tracking-[0.24em] text-amber-200/70">
                        FINALS DAY • {finalsDayLabel}
                      </div>
                      <div className="mt-1 text-sm font-black uppercase tracking-[0.08em] text-white sm:text-base">
                        Finals start {RING_START_TIME}
                      </div>
                    </div>
                  )}

                <article
                  id={`fight-${Number(activeRing)}-${Number(match.fight_number)}`}
                  className={`scroll-mt-24 overflow-hidden rounded-xl border bg-[#0d141f]/95 shadow-[0_10px_28px_rgba(0,0,0,0.22)] transition-all duration-300 ${
                    isHighlighted
                      ? "border-amber-300/65 ring-2 ring-amber-300/15"
                      : isNext
                      ? "border-amber-200/25"
                      : "border-white/[0.075]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 border-b border-white/[0.055] bg-white/[0.018] px-3 py-2 sm:px-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="shrink-0 text-[18px] font-black tabular-nums text-amber-200 sm:text-[21px]">
                        {match.fight_number}
                      </div>
                      <div className="h-4 w-px bg-white/[0.08]" />
                      <div className="truncate text-[9px] font-black uppercase tracking-[0.16em] text-slate-500 sm:text-[10px]">
                        {getRoundLabel(match)}
                      </div>

                      {isFemaleFight(match.gender) && (
                        <span className="shrink-0 rounded-md border border-pink-300/35 bg-pink-400/10 px-2 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-pink-200 shadow-[0_0_12px_rgba(244,114,182,0.10)] sm:text-[9px]">
                          FEMALE
                        </span>
                      )}

                      {timeInfo?.time && (
                        <>
                          <div className="h-4 w-px bg-white/[0.08]" />
                          <div
                            className={`whitespace-nowrap text-[9px] font-black uppercase tracking-[0.10em] sm:text-[10px] ${
                              timeInfo.kind === "actual"
                                ? "text-emerald-300/80"
                                : "text-cyan-200/70"
                            }`}
                          >
                            {timeInfo.kind === "actual" ? "DONE " : " "}
                            {formatEventTime(timeInfo.time)}
                          </div>
                        </>
                      )}
                    </div>

                    <StatusBadge completed={completed} isNext={isNext} />
                  </div>

                  <div className="relative grid grid-cols-[minmax(0,1fr)_56px_minmax(0,1fr)] items-stretch sm:grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)]">
                    <CornerSide
                      corner={red}
                      side="red"
                      onSourceFightClick={scrollToFight}
                      isWinner={redIsWinner}
                      isLoser={redIsLoser}
                    />

                    <div className="relative z-10 flex items-center justify-center px-0.5 sm:px-1">
                      <div
                        className={`min-w-[52px] rounded-lg border px-1.5 py-2 text-center shadow-[0_6px_18px_rgba(0,0,0,0.28)] sm:min-w-[66px] sm:px-2 ${
                          isFemaleFight(match.gender)
                            ? "border-pink-300/35 bg-pink-500/[0.10]"
                            : "border-white/[0.09] bg-[#070b12]/88"
                        }`}
                      >
                        <div
                          className={`whitespace-nowrap text-[9px] font-black uppercase tracking-[0.05em] sm:text-[10px] ${
                            isFemaleFight(match.gender)
                              ? "text-pink-200"
                              : "text-slate-300"
                          }`}
                        >
                          {match.age_division || "-"}
                        </div>
                        <div className="mt-0.5 whitespace-nowrap text-[10px] font-black uppercase text-amber-100 sm:text-[12px]">
                          {match.weightclass_name || "-"}
                        </div>
                        <div
                          className={`mt-1 whitespace-nowrap text-[7px] font-black uppercase tracking-[0.08em] ${
                            getDivisionTypeLabel(match.type) === "АЛТАН ГАРАА"
                              ? "text-amber-300"
                              : "text-cyan-300/80"
                          }`}
                        >
                          {getDivisionTypeLabel(match.type)}
                        </div>
                      </div>
                    </div>

                    <CornerSide
                      corner={blue}
                      side="blue"
                      onSourceFightClick={scrollToFight}
                      isWinner={blueIsWinner}
                      isLoser={blueIsLoser}
                    />
                  </div>
                </article>
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="py-8 text-center text-[9px] font-black uppercase tracking-[0.22em] text-slate-700 sm:text-[10px]">
          WAR FC • Warrior Athletes Rising • Official Fight Schedule
        </div>
      </main>

      {nextFight && (
        <button
          type="button"
          onClick={() => scrollToFight(nextFight.fight_number)}
          className="
            fixed
            bottom-[calc(env(safe-area-inset-bottom)+5.5rem)]
            right-3 z-[60]
            flex items-center gap-2.5
            rounded-2xl border border-amber-300/40
            bg-[#0b111b]/95 px-3 py-2.5
            shadow-[0_12px_38px_rgba(0,0,0,0.50)]
            backdrop-blur-xl
            transition-all active:scale-95
            sm:bottom-6 sm:right-6 sm:gap-3 sm:px-4 sm:py-3
          "
        >
          <div className="flex h-9 min-w-9 items-center justify-center rounded-xl bg-amber-300 px-2 text-lg font-black tabular-nums text-black">
            {nextFight.fight_number}
          </div>

          <div className="text-left">
            <div className="text-[8px] font-black uppercase tracking-[0.20em] text-amber-300/70">
              Next Fight
            </div>
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-300">
                  {nextFight.age_division || "-"}
                  <br />
                  <span className="text-white">{nextFight.weightclass_name || "-"}</span>
                </div>
          </div>

          <div className="text-base text-amber-300">↓</div>
        </button>
      )}
    </div>
  );
}
 